import React, { createContext, FC, ReactNode, useCallback, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { AuthService } from 'lib';
import { USER_TOKEN_NAME } from 'lib/AuthService';
import { routeConstants } from 'routes';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { logOutRequest, selectCurrentEmployee, setCurrentEmployeeRequest } from 'store/App';
import { Employers, getEmployersRequest, selectEmployers } from 'store/Employers';
import { IEmployerState, IStore, IUserState } from 'store/types';
import { getCurrentUserRequest, selectCurrentUser, User } from 'store/User';
import { findFirstManagementEmployer } from 'utils/employers';

interface IProps {
  children: ReactNode;
}

interface IAppContext {
  isLoading: boolean;
  user: IUserState;
  employers: Record<string, IEmployerState>;
}

const AppContext = createContext<IAppContext | null>(null);

export const useAppContext = () => useContext(AppContext);

export const AppProvider: FC<IProps> = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const user = useSelector(selectCurrentUser());
  const employers = useSelector(selectEmployers);
  const currentEmployee = useSelector(selectCurrentEmployee());
  const isUserLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.GET_CURRENT_USER_REQUEST));
  const isEmployersLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employers.GET_EMPLOYERS_REQUEST));

  // Handle changes in local storage, specifically for user token.
  const handleStorageChange = useCallback(
    (e: StorageEvent) => {
      if (e.key === USER_TOKEN_NAME) {
        // Force logout when the new value of the token is null.
        if (e.newValue === null) dispatch(logOutRequest(`${routeConstants.public.login}?message=logged_out`));
      }
    },
    [dispatch]
  );

  // Add event listener to local storage.
  useEffect(() => {
    window.addEventListener('storage', handleStorageChange, false);
    return () => window.removeEventListener('storage', handleStorageChange, false);
  }, [handleStorageChange]);

  // Load the current user & employers.
  // This gives us context of the user & the employers for the user on every page in the application.
  // ONLY do this when a token is present
  useEffect(() => {
    const token = AuthService.getToken();
    if (token) {
      if (!user?.id && !isUserLoading) dispatch(getCurrentUserRequest());
      if (!employers && !isEmployersLoading) dispatch(getEmployersRequest());
    }
  }, [location.pathname]);

  // Auto-select the current employer
  // ONLY do this when:
  // 1. The currentEmployee is not set.
  // 2. The logged in user is for a remote session.
  // 3. Employers have been loaded.
  useEffect(() => {
    if (!currentEmployee && user && user.remoteSession && employers) {
      // Convert the employers object to an array.
      const employersArray = Object.values(employers).map((entry) => ({ ...entry, sites: Object.values(entry.sites) }));

      // Find the first management employer in the employer list.
      const employee = findFirstManagementEmployer(employersArray);

      // When a suitable employee is found, dispatch an action to set the current employee.
      if (employee) dispatch(setCurrentEmployeeRequest(employee));
    }
  }, [user, employers, currentEmployee, dispatch]);

  const isLoading = !user || !user.id || !employers;

  return <AppContext.Provider value={{ user, employers, isLoading }}>{children}</AppContext.Provider>;
};
